import type { ReactNode } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { isPartsRole } from "../../../shared/domain.js";
import { useSession } from "../../app/AppProviders.js";
import { roleHomePath } from "../../app/RoleHomeRedirect.js";
import { WorkspaceFrame } from "../cases/workspace/WorkspaceFrame.js";
import { type WholesalePageDestination, type WholesaleView } from "./presentation.js";
import { WholesaleSidebar, wholesaleViewPath } from "./WholesaleSidebar.js";
import { parseWholesaleUrlState, type WholesaleUrlState, type WholesaleUrlStateApi } from "./useWholesaleUrlState.js";

/**
 * The frame every wholesale page sits in (spec §4.1): the shared
 * `WorkspaceFrame` — top bar with the menu toggle, the scrim under the
 * folded menu, `main` for the page, desktop control sizes — with the
 * wholesale sidebar, framing the workspace, the order form, the customers
 * page, and the catalog and report when they are opened from this side.
 *
 * Landing on any wholesale address remembers the side and the place for
 * this browser (the frame does it), so a bookmark pinned to `/wholesale`
 * reopens there next time and the switch returns here. The shell is
 * Parts' own: any other role is sent to the workspace its role starts on,
 * and never sees the word Wholesale. The `PartsShell` is its twin on the
 * service side.
 */

const PAGE_STATE: WholesaleUrlState = parseWholesaleUrlState(new URLSearchParams());

export interface WholesaleShellProps {
  /** The workspace passes its URL-state API; the pages leave it out and get plain links. */
  url?: WholesaleUrlStateApi;
  activeDestination?: WholesaleView | WholesalePageDestination;
  /** `workspace--page` for a single scrolling column (form, customers, catalog, report). */
  page?: boolean;
  className?: string;
  /** Class for the top bar, the sidebar and the scrim (the report keeps them off paper). */
  chromeClassName?: string;
  mainClassName?: string;
  children: ReactNode;
}

export function WholesaleShell({
  url,
  activeDestination,
  page = false,
  className,
  chromeClassName,
  mainClassName,
  children,
}: WholesaleShellProps) {
  const { user } = useSession();
  const navigate = useNavigate();

  if (user && !isPartsRole(user.role)) {
    return <Navigate to={roleHomePath(user.role)} replace />;
  }

  const state = url?.state ?? PAGE_STATE;

  return (
    <WorkspaceFrame
      side="wholesale"
      page={page}
      {...(className !== undefined ? { className } : {})}
      {...(chromeClassName !== undefined ? { chromeClassName } : {})}
      {...(mainClassName !== undefined ? { mainClassName } : {})}
      sidebar={(menu) => (
        <WholesaleSidebar
          id={menu.id}
          open={menu.open}
          state={state}
          {...(activeDestination !== undefined ? { activeDestination } : {})}
          onSelectView={(view) => (url ? url.setView(view) : navigate(wholesaleViewPath(state, view)))}
          onNavigate={menu.onNavigate}
          {...(chromeClassName !== undefined ? { className: chromeClassName } : {})}
        />
      )}
    >
      {children}
    </WorkspaceFrame>
  );
}
